import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Login as LoginIcon,
  Visibility,
  VisibilityOff,
  PersonAdd as SignUpIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const LoginForm: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();
  const theme = useTheme();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      setError('Please enter both email and password');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await login(email.trim(), password);
    } catch (err) {
      setError('Invalid credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      className="jb-auth-root"
      sx={{
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'center',
      }}
    >
      {/* Background layers (matching landing page hero) */}
      <div className="jb-auth-layer jb-auth-grid" />
      <div className="jb-auth-layer jb-auth-conic" />
      <div className="jb-auth-blob jb-auth-blob--1" />
      <div className="jb-auth-blob jb-auth-blob--2" />
      <div className="jb-auth-blob jb-auth-blob--3" />

      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          width: '100%',
          height: '100vh',
          flexDirection: { xs: 'column', md: 'row' },
        }}
      >
        {/* Left Panel - Branding with Theme Color */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            p: { xs: 3, md: 6 },
            background: `linear-gradient(135deg,
              ${alpha(theme.palette.primary.main, 0.92)} 0%,
              ${alpha(theme.palette.secondary.main, 0.72)} 55%,
              ${alpha(theme.palette.warning.main, 0.42)} 100%)`,
            color: 'white',
            textAlign: 'center',
            minHeight: { xs: '40vh', md: '100vh' },
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              backgroundImage:
                'linear-gradient(to right, rgba(255,255,255,0.14) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.14) 1px, transparent 1px)',
              backgroundSize: '26px 26px',
              opacity: 0.35,
              pointerEvents: 'none',
            }}
          />

          <Box
            sx={{
              width: 120,
              height: 120,
              borderRadius: '50%',
              background: alpha(theme.palette.common.white, 0.2),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3,
              backdropFilter: 'blur(10px)',
              border: `1px solid ${alpha(theme.palette.common.white, 0.3)}`,
              position: 'relative',
              zIndex: 1,
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                color: 'white',
                textShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
              }}
            >
              J
            </Typography>
          </Box>

          <Typography
            variant="h2"
            sx={{
              fontWeight: 700,
              mb: 2,
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
              letterSpacing: '-0.02em',
              position: 'relative',
              zIndex: 1,
            }}
          >
            JackBot
          </Typography>

          <Typography
            variant="h6"
            sx={{
              opacity: 0.9,
              lineHeight: 1.6,
              mb: 4,
              fontWeight: 400,
              maxWidth: '400px',
              color: 'white',
              position: 'relative',
              zIndex: 1,
            }}
          >
            Manage your AI chatbot integrations and customer interactions with ease
          </Typography>

          <Box sx={{ textAlign: 'left', maxWidth: '300px', position: 'relative', zIndex: 1 }}>
            {[
              'Customer Management',
              'Widget Configuration', 
              'Analytics & Insights',
              'Real-time Chat Support'
            ].map((feature, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mb: 1.5,
                  opacity: 0.9,
                }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: alpha(theme.palette.common.white, 0.8),
                    mr: 2,
                  }}
                />
                <Typography variant="body1">{feature}</Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Right Panel - Login Form with White Background */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: { xs: 3, md: 4 },
            bgcolor: 'transparent',
            minHeight: { xs: '60vh', md: '100vh' },
          }}
        >
          <Paper
            elevation={0}
            sx={{
              width: '100%',
              maxWidth: 440,
              px: { xs: 3, md: 4 },
              py: { xs: 3, md: 4 },
              borderRadius: 4,
              backgroundColor: alpha(theme.palette.common.white, 0.78),
              border: `1px solid ${alpha(theme.palette.common.white, 0.45)}`,
              backdropFilter: 'blur(16px)',
              boxShadow: '0 20px 60px rgba(17, 24, 39, 0.12)',
            }}
          >
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  mb: 1,
                }}
              >
                Welcome Back
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Sign in to access your admin dashboard
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} noValidate>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                margin="normal"
                required
                autoComplete="email"
                autoFocus
                disabled={loading}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                margin="normal"
                required
                autoComplete="current-password"
                disabled={loading}
                sx={{ mb: 3 }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <LoginIcon />}
                sx={{
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  mb: 3,
                }}
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>

              <Divider sx={{ my: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  OR
                </Typography>
              </Divider>

              <Button
                fullWidth
                variant="outlined"
                size="large"
                startIcon={<SignUpIcon />}
                onClick={() => navigate('/signup')}
                disabled={loading}
                sx={{
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 500,
                }}
              >
                Create New Account
              </Button>
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default LoginForm; 